<template>
    <div 
        class="col-md-12"
        :class="{'px-0' : $breakpoint.smAndDown}"
    >
        <!-- Personal information -->
        <form class="form-row" @submit.prevent>
            <div class="col-md-12"
                :class="{'px-0' : $breakpoint.smAndDown}">
                <!-- PERSONAL INFORMATION -->
                <div class="form-row">
                    <h2 class="heading-medium">Personal Information</h2>
                </div>

                <div class="form-row my-2">
                    <div 
                        class="form-group col-lg-4 col-md-6"
                        :class="{'px-0' : $breakpoint.smAndDown}"
                        v-for="(input, idx) in userInputs" :key="idx"
                    >
                        <label :class="{'error': input.isInvalid || input.isDuplicate}">{{ input.label }}{{ input.isRequired ? "*" : "" }}</label>
                        <input :name="input.field" class="form-control" type="text" v-model="user[input.field]" @blur="validateInput('PERSONAL', input.id)" @input="checkIsFormComplete()">
                        <div class="invalid-input">
                            <i class="fas fa-exclamation"></i>
                            {{ input.errorMessages[input.id === 'EMAIL' && input.isDuplicate ? 1 : 0] }}
                        </div>
                    </div>
                </div>

                <!-- ACCOUNT ADMIN ONLY -->
                <div v-if="userRoles.type_id === 'admin'" class="form-row my-2">
                    <!-- USER ROLE -->
                    <div v-if="isModal" class="form-group col-lg-4 col-md-6" :class="{'px-0' : $breakpoint.smAndDown}">
                        <label class="d-block w-100">Role</label>
                        <input type="checkbox" class="mr-2" v-model="user.isAdmin" @change="checkIsFormComplete()" />Administrator
                    </div>
                </div>

                <!-- ADDRESS INFORMATION -->
                <div class="form-row">
                    <h2 class="heading-medium">Address Information</h2>
                    <div class="col-md-12 d-flex justify-content-start my-2">
                        <input 
                            type="checkbox" 
                            v-model="useIndividualAddress"
                            @change.prevent="setUserAddress($event.target.checked)"
                        />
                        Use individual address
                    </div>
                </div>

                <div class="form-row my-2" v-if="account.address">
                    <div 
                        class="form-group col-lg-4 col-md-6"
                        :class="{'px-0' : $breakpoint.smAndDown}"
                        v-for="(input, idx) in addressInputs" :key="idx"
                    >
                        <label :class="{'error': input.isInvalid }">{{ input.label }}{{ input.isRequired ? "*" : "" }}</label>
                        <input :name="input.field" class="form-control" type="text" v-model="input.value" @blur="validateInput('ADDRESS', input.id)" @input="checkIsFormComplete()"
                            :disabled="!useIndividualAddress">
                        <div class="invalid-input">
                            <i class="fas fa-exclamation"></i>
                            {{ input.errorMessages[0] }}
                        </div>
                    </div>
                </div>
            </div>
            <div class="form-group modal-button-group text-center col-12"
                :class="{'px-2 mb-3' : $breakpoint.smAndDown}">
                <p v-if="connectionError" class="text-danger pt-2">{{ connectionError }}</p>
                <button v-if="isModal" class="btn btn-tertiary mr-2" id="cancelButton" @click="closeModal()" data-dismiss="modal">Cancel</button>
                <button type="submit" class="btn btn-primary" :disabled="!isFormComplete" @click.prevent="saveUserDetails()">Save details</button>
            </div>
        </form>
    </div>
</template>

<script>
import breakpoint from '@/mixins/index';
import { mapState, mapActions } from "vuex";
import { _isInputValid } from "@/util/validation.js";

export default {
    mixins: [breakpoint],
    props: {
        user: Object,
        account: Object,
        type: {
            type: String,
            default: "",
            required: false
        },
        isModal: Boolean
    },
    data(){
        return {
            userInputs: [
                {
                    field: "firstName",
                    id: "FIRST_NAME",
                    isInvalid: false,
                    label: "First name",
                    errorMessages: ["Please enter a first name"],
                    isRequired: true
                },
                {
                    field: "lastName",
                    id: "LAST_NAME",
                    isInvalid: false,
                    label: "Last name",
                    errorMessages: ["Please enter a last name"],
                    isRequired: true
                },
                {
                    field: "email",
                    id: "EMAIL",
                    isInvalid: false,
                    isDuplicate: false,
                    label: "Email address",
                    errorMessages: ["Please enter a valid email address", "This email has already been taken"],
                    isRequired: true
                },
                {
                    field: "phone",
                    id: "PHONE",
                    isInvalid: false,
                    label: "Phone number",
                    errorMessages: ["Please enter a valid phone number"],
                    isRequired: false
                },
                {
                    field: "mobile",
                    id: "MOBILE",
                    isInvalid: false,
                    label: "Mobile number",
                    errorMessages: ["Please enter a valid mobile number"],
                    isRequired: false
                },
            ],
            addressInputs: [
                {
                    field: "addressLine1",
                    id: "ADDRESS_LINE_1",
                    isInvalid: false,
                    label: "Address Line 1",
                    errorMessages: ["Please enter first line of address"],
                    isRequired: true,
                    value: ""
                },
                {
                    field: "addressLine2",
                    id: "ADDRESS_LINE_2",
                    isInvalid: false,
                    label: "Address Line 2",
                    errorMessages: ["Please enter second line of address"],
                    isRequired: true,
                    value: ""
                },
                {
                    field: "postCode",
                    id: "POSTCODE",
                    isInvalid: false,
                    label: "Postcode",
                    errorMessages: ["Please enter a valid postcode"],
                    isRequired: true,
                    value: ""
                },
                {
                    field: "country",
                    id: "COUNTRY",
                    isInvalid: false,
                    label: "Country",
                    errorMessages: ["Please enter a country name"],
                    isRequired: true,
                    value: ""
                }
            ],
            useIndividualAddress: false,
            connectionError: "",
            hasFinishedPopulating: false,
            isNewUser: false,
            isFormComplete: false
        }
    },
    created(){
        this.isNewUser = Object.keys(this.user).length === 0; // If user is an empty object, we are creating a new user
    },
    computed: {
        ...mapState('accounts', ['userRoles']),
        ...mapState('login', ['userLoggedIn']),
        checkUserAddressSelection: function(){
            if (this.account && this.account.address && this.user && !this.hasFinishedPopulating){ // Once data has been loaded, check wheter user is using individual address or not
                const checked = this.user.address && (Object.keys(this.user.address).length > 0);
                this.useIndividualAddress = checked; // Check/uncheck the checkbox
                this.setUserAddress(checked); // Assign user address to the input fields
                this.hasFinishedPopulating = true; // Notify the component that we are not expecting any data population anymore
            }
        }
    },
    watch: {
        checkUserAddressSelection(){}
    },
    methods: {
        ...mapActions('notifications', ['displayActionConfirmation']),
        validateInput: function(section, id){
            let ui, val;

            if (section === "ADDRESS"){ // Target address data to validate
                ui = this.addressInputs.find(ui => ui.id === id);
                val = ui.value ? ui.value.trim() : "";
            } else { // Target personal information data to validate
                ui = this.userInputs.find(ui => ui.id === id);
                val = this.user[ui.field] ? this.user[ui.field].trim() : "";
            }

            if (!ui.isRequired && !val) // If this is a non-required field and it's empty, we don't need to validate input
                return ui.isInvalid = false; // Remove any error messsages related to this input field
            else
                ui.isInvalid = val ? !_isInputValid(val, id) : true;
                    
            if (id === "EMAIL") // If this is for email, remove any error messages regarding unique email address
                ui.isDuplicate = false;

            this.checkIsFormComplete(); // Let's check whether form is ready to submit or not
        },
        checkIsFormComplete: function(){
            let requirements = this.user.firstName && this.user.lastName && _isInputValid(this.user.email, "EMAIL"); // Required fields are filled in with valid details

            if (this.user.phone)
                requirements = requirements && _isInputValid(this.user.phone, "PHONE"); // Validate phone number if user inputs any

            if (this.user.mobile)
                requirements = requirements && _isInputValid(this.user.mobile, "MOBILE"); // Validate mobile number if user inputs any

            if (this.useIndividualAddress) // Check for the following requirements if user is using individual address
                requirements = requirements && 
                                this.addressInputs.find(ai => ai.id === "ADDRESS_LINE_1").value && // Address line 1 is filled in
                                this.addressInputs.find(ai => ai.id === "ADDRESS_LINE_2").value && // Address line 2 is filled in
                                _isInputValid(this.addressInputs.find(ai => ai.id === "POSTCODE").value, "POSTCODE") && // Validate postcode
                                _isInputValid(this.addressInputs.find(ai => ai.id === "COUNTRY").value, "COUNTRY"); // Validate country name
            
            this.isFormComplete = requirements;
        },
        setUserAddress: function(checked){
            this.addressInputs.forEach(ui => {
                if (checked) 
                    ui.value = this.user.address ? this.user.address[ui.field] : "";
                else 
                    ui.value = this.account.address[ui.field];

                ui.isInvalid = false;
            });

            if (this.hasFinishedPopulating)
                this.checkIsFormComplete(); // Let's check whether form is ready to submit or not
        },
        saveUserDetails: async function(){
            let payload = {
                firstName: this.user.firstName.trim(),
                lastName: this.user.lastName.trim(),
                email: this.user.email.trim(),
                phone: this.user.phone ? this.user.phone.trim() : "",
                mobile: this.user.mobile ? this.user.mobile.trim() : "",
                useIndividualAddress: this.useIndividualAddress
            };

            if (this.isModal){
                payload.typeId = this.user.isAdmin ? "admin" : "user";

                if (!this.isNewUser && this.user._id !== this.userLoggedIn.userId) // User is editing another team member
                    payload.editedUserId = this.user._id;
                else if (this.isNewUser)
                    payload.username = this.user.email.trim(); // Set the email as the new user's username
            }

            if (this.useIndividualAddress){
                payload.addressLine1 = this.addressInputs.find(ai => ai.id === "ADDRESS_LINE_1").value.trim();
                payload.addressLine2 = this.addressInputs.find(ai => ai.id === "ADDRESS_LINE_2").value.trim();
                payload.postCode = this.addressInputs.find(ai => ai.id === "POSTCODE").value.trim();
                payload.country = this.addressInputs.find(ai => ai.id === "COUNTRY").value.trim();
            }

            try {
                let response;

                if (this.isNewUser){ // Creating a new user
                    response = await this.axios.post("/api/createUser", payload);
                    this.displayActionConfirmation({shown: true, message: "User created"});
                } else { // Editing an existing user
                    response = await this.axios.put("/api/saveUserDetails", payload);
                    this.displayActionConfirmation({shown: true, message: "Changes saved"});
                }

                this.$emit("updateUser");

                // Now let's remove any error messages
                this.userInputs.forEach(ui => {
                    ui.isInvalid = false;
                });
                this.isFormComplete = false; // Let's disable the save button again now
            } catch(error) {
                if (error.response.status === 400)
                    this.userInputs.find(ui => ui.id === "EMAIL").isDuplicate = true; // User has entered a duplicate email address
                else if (error.response.status === 403)
                    this.connectionError = "You’re not authorised to perform this action. Your permissions may have changed."
                else
                    this.connectionError = "Sorry, we are unable to process your request currently. Please try again later."; // Notify to user that there is a server error
            }
        },
        closeModal: function(){
            this.$emit("closeUserInformation");
        }
    }
}
</script>

<style lang="scss" scoped>

</style>